/**
 * Support for the MacOS/iOS secure wrapper(native app that hosts the PWA in a
 * WKWebView). The wrapper owns the secure environment, so it -
 *  - identifies itself by appending SECUREMACOSiOS to the user agent
 *  - dispatches window events on the PWA when the tester needs to be exited
 *  - listens on the 'secureTesting' webkit message handler for our exit request
 */
import { BROWSER } from '../DisplayMode';
import { MACOS } from '../Platform';

export const SECURE_MACOS_iOS = 'SECUREMACOSiOS';

/**
 * The wrapper runs the PWA as a regular(BROWSER) page, so the user agent is the
 * only way to tell it apart from Chrome/Safari on MacOS.
 *
 * @param {*} platform Platform the PWA is running on
 * @param {*} displayMode Display mode(not the displayMode object) the PWA is in
 * @returns true when running inside the MacOS/iOS secure wrapper
 */
export const checkForSecureWrapper = (platform, displayMode) => {
  if (
    platform === MACOS &&
    displayMode === BROWSER &&
    navigator.userAgent.includes(SECURE_MACOS_iOS)
  ) {
    return true;
  }
  return false;
};

/**
 * Asks the wrapper to tear down secure testing and close itself. Nothing to do
 * when the handler is missing - we are either not inside the wrapper or it is
 * an older version that does not expose it.
 */
export const exitSecureWrapper = () => {
  const handler =
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.secureTesting;

  if (handler && handler.postMessage) {
    handler.postMessage({ action: 'requestExitSecureTesting' });
  }
};

/**
 * If there is a exitCallback it is the use case where the user is testing, otherwise the user
 * is still on the landing or login screen or waiting to start the test(nothing to
 * save, so just hand control back to the wrapper).
 *
 * @param {*} exitCallback Handles the events that should be called for exiting. Eg. Saving test
 */
export const exitHandler = (exitCallback) => {
  if (exitCallback) {
    exitCallback();
  } else {
    exitSecureWrapper();
  }
};

/**
 * Listens for the exit events dispatched by the wrapper -
 *  - secureWrapperForceExit: the wrapper is shutting secure testing down
 *  - secureWrapperAssessmentInterrupted: something interrupted the secure environment
 *
 * Both are handled the same way(save and exit), they are kept as separate
 * listeners because the wrapper dispatches them as two distinct window events.
 *
 * @param {*} exitCallback see exitHandler
 * @returns the handlers that were attached, needed by removeEventListeners
 */
export const registerEvents = (exitCallback) => {
  //Keep a reference to each handler, the arrow functions cannot be removed otherwise
  let eventHandlers = {
    secureWrapperForceExit: () => exitHandler(exitCallback),
    secureWrapperAssessmentInterrupted: () => exitHandler(exitCallback),
  };
  window.addEventListener(
    'secureWrapperForceExit',
    eventHandlers.secureWrapperForceExit,
  );
    window.addEventListener(
    'secureWrapperAssessmentInterrupted',
    eventHandlers.secureWrapperAssessmentInterrupted,
  );
  return eventHandlers;
};

export const removeEventListeners = (eventHandlers) => {
  window.removeEventListener(
    'secureWrapperForceExit',
    eventHandlers?.secureWrapperForceExit,
  );
  window.removeEventListener(
    'secureWrapperAssessmentInterrupted',
    eventHandlers?.secureWrapperAssessmentInterrupted,
  );
};
